import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AuthService from '../services/authService';
import {
  applyPreservedSearchParams,
  clearedSearchParamsPreservingPreview,
  syncMarketPulsePreviewFromUrl,
} from '../utils/marketPulsePreview';

function authHeaders(): HeadersInit {
  const token = AuthService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchWithAuth(url: string): Promise<Response> {
  return fetch(url, { headers: authHeaders() });
}

interface Branch {
  id: number;
  name: string;
  brandId: number;
  currency: string;
}

interface BranchContextType {
  selectedBranch: Branch | null;
  setSelectedBranch: (branch: Branch | null) => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

interface BranchProviderProps {
  children: ReactNode;
}

function normalizeBranch(raw: Record<string, unknown> | null | undefined): Branch | null {
  if (!raw) return null;

  const id = raw.id ?? raw.BranchID;
  const brandId = raw.brandId ?? raw.BrandId;
  if (id == null || brandId == null) return null;

  return {
    id: Number(id),
    name: String(raw.name ?? raw.BranchName ?? ''),
    brandId: Number(brandId),
    currency: String(raw.currency ?? raw.Currency ?? ''),
  };
}

async function resolveBranch(brandId: number, branchId: number): Promise<Branch | null> {
  try {
    const directResponse = await fetchWithAuth(`/api/branches/${branchId}`);
    if (directResponse.ok) {
      const directBranch = normalizeBranch(await directResponse.json());
      if (directBranch) return directBranch;
    }
  } catch {
    // fallback to brand-scoped lookup below
  }

  const branchesResponse = await fetchWithAuth(`/api/branches/brand/${brandId}`);
  if (!branchesResponse.ok) {
    throw new Error('Failed to fetch branches');
  }

  const branches = await branchesResponse.json();
  const list = Array.isArray(branches) ? branches : [];
  return list.map((item) => normalizeBranch(item)).find((item): item is Branch => item !== null && item.id === branchId) ?? null;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  syncMarketPulsePreviewFromUrl();

  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchLicenses } = useAuth();

  const prevBranchIdRef = useRef<number | null>(null);
  const prevLicenseFetchIdRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const isUpdatingFromURLRef = useRef(false);

  useEffect(() => {
    syncMarketPulsePreviewFromUrl();
  }, []);

  // 1.1 & 1.2: Read and validate URL parameters on initialization
  useEffect(() => {
    if (isInitializedRef.current) return;

    const urlBrandId = searchParams.get('brandId');
    const urlBranchId = searchParams.get('branchId');

    if (!urlBrandId || !urlBranchId) {
      isInitializedRef.current = true;
      return;
    }

    const brandId = parseInt(urlBrandId, 10);
    const branchId = parseInt(urlBranchId, 10);

    if (isNaN(brandId) || isNaN(branchId)) {
      console.error('Invalid URL parameters: brandId and branchId must be numeric');
      setSearchParams((prev) => clearedSearchParamsPreservingPreview(prev), { replace: true });
      isInitializedRef.current = true;
      return;
    }

    const validateAndFetchBranch = async () => {
      try {
        isUpdatingFromURLRef.current = true;

        const brandsResponse = await fetchWithAuth('/api/brands');
        if (!brandsResponse.ok) {
          throw new Error('Failed to fetch brands');
        }
        const brands = await brandsResponse.json();
        const brandExists = brands.some((brand: { id?: number; BrandID?: number }) => {
          const id = brand.id ?? brand.BrandID;
          return Number(id) === brandId;
        });

        if (!brandExists) {
          console.error(`Brand with ID ${brandId} not found`);
          setSearchParams((prev) => clearedSearchParamsPreservingPreview(prev), { replace: true });
          return;
        }

        const branch = await resolveBranch(brandId, branchId);
        if (!branch) {
          console.error(`Branch with ID ${branchId} not found`);
          setSearchParams((prev) => clearedSearchParamsPreservingPreview(prev), { replace: true });
          return;
        }

        setSelectedBranch(branch);
      } catch (error) {
        console.error('Error validating branch from URL:', error);
        setSearchParams((prev) => clearedSearchParamsPreservingPreview(prev), { replace: true });
      } finally {
        isUpdatingFromURLRef.current = false;
        isInitializedRef.current = true;
      }
    };

    validateAndFetchBranch();
  }, []); // Run only once on mount

  // 1.3: Update URL when branch selection changes
  useEffect(() => {
    if (!isInitializedRef.current || isUpdatingFromURLRef.current) return;

    const currentId = selectedBranch?.id;

    if (currentId && currentId !== prevBranchIdRef.current) {
      setSearchParams(
        (prev) => {
          const next = applyPreservedSearchParams(prev);
          next.set('brandId', String(selectedBranch.brandId));
          next.set('branchId', String(selectedBranch.id));
          return next;
        },
        { replace: true },
      );
      prevBranchIdRef.current = currentId;
    } else if (!currentId && prevBranchIdRef.current) {
      setSearchParams((prev) => clearedSearchParamsPreservingPreview(prev), { replace: true });
      prevBranchIdRef.current = null;
    }
  }, [selectedBranch, setSearchParams]);

  // 1.4: Handle browser navigation (back/forward buttons)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    const urlBrandId = searchParams.get('brandId');
    const urlBranchId = searchParams.get('branchId');

    const urlBranchIdNum = urlBranchId ? parseInt(urlBranchId, 10) : null;
    const currentBranchId = selectedBranch?.id || null;

    if (urlBranchIdNum && urlBranchIdNum !== currentBranchId) {
      const brandId = parseInt(urlBrandId!, 10);

      if (isNaN(brandId) || isNaN(urlBranchIdNum)) {
        return;
      }

      const fetchBranchFromURL = async () => {
        try {
          isUpdatingFromURLRef.current = true;
          const branch = await resolveBranch(brandId, urlBranchIdNum);
          if (branch) {
            setSelectedBranch(branch);
          }
        } catch (error) {
          console.error('Error fetching branch from URL navigation:', error);
        } finally {
          isUpdatingFromURLRef.current = false;
        }
      };

      fetchBranchFromURL();
    } else if (!urlBranchId && selectedBranch) {
      isUpdatingFromURLRef.current = true;
      setSelectedBranch(null);
      isUpdatingFromURLRef.current = false;
    }
  }, [searchParams]);

  useEffect(() => {
    const currentId = selectedBranch?.id;
    if (currentId && currentId !== prevLicenseFetchIdRef.current) {
      fetchLicenses(currentId);
      prevLicenseFetchIdRef.current = currentId;
    }
  }, [selectedBranch?.id, fetchLicenses]);

  return (
    <BranchContext.Provider value={{ selectedBranch, setSelectedBranch }}>
      {children}
    </BranchContext.Provider>
  );
};
